import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  move_date: "",
  move_from: "",
  move_to: "",
  move_type: "residential",
  size: "",
  stairs: "",
  special_items: "",
  packing_needs: "",
  message: "",
};

const QuoteForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, form);
      setSubmitted(true);
      toast.success("Quote request sent! We'll call you shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please call us at (307) 265-8291.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-white border border-[var(--ct-border)] rounded-lg px-4 py-3 focus:ring-2 focus:ring-[var(--ct-primary)] focus:border-transparent outline-none transition-all w-full text-[var(--ct-text)]";
  const labelCls = "text-xs font-bold uppercase tracking-[0.15em] text-[var(--ct-primary)] mb-2 block";

  if (submitted) {
    return (
      <div data-testid="quote-success" className="rounded-3xl bg-white p-10 text-center border border-[var(--ct-border)] shadow-[0_20px_40px_rgba(0,0,0,0.06)]">
        <div className="w-16 h-16 rounded-full bg-[var(--ct-primary)] text-white flex items-center justify-center mx-auto">
          <Send className="w-7 h-7" />
        </div>
        <h3 className="mt-5 text-2xl font-semibold text-[var(--ct-primary)]" style={{ fontFamily: "Outfit" }}>
          Thanks! Your quote request is in.
        </h3>
        <p className="mt-2 text-[var(--ct-text-muted)]">
          A Crosstown Movers team member will reach out shortly at the number provided. Need to chat now? Call{" "}
          <a className="font-semibold text-[var(--ct-secondary)]" href="tel:+13072658291">(307) 265-8291</a>.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="btn-outline mt-6"
          data-testid="quote-submit-another"
        >
          Submit another quote
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className="rounded-3xl bg-white p-7 md:p-10 border border-[var(--ct-border)] shadow-[0_20px_60px_rgba(0,0,0,0.08)]"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full Name" required>
          <input name="name" value={form.name} onChange={onChange} required className={inputCls} placeholder="Jane Doe" data-testid="quote-name" />
        </Field>
        <Field label="Phone" required>
          <input name="phone" value={form.phone} onChange={onChange} required className={inputCls} placeholder="(307) 555-0123" data-testid="quote-phone" />
        </Field>
        <Field label="Email" required>
          <input type="email" name="email" value={form.email} onChange={onChange} required className={inputCls} placeholder="you@email.com" data-testid="quote-email" />
        </Field>
        <Field label="Preferred Move Date">
          <input type="date" name="move_date" value={form.move_date} onChange={onChange} className={inputCls} data-testid="quote-date" />
        </Field>
        <Field label="Moving From" required>
          <input name="move_from" value={form.move_from} onChange={onChange} required className={inputCls} placeholder="Casper, WY" data-testid="quote-from" />
        </Field>
        <Field label="Moving To" required>
          <input name="move_to" value={form.move_to} onChange={onChange} required className={inputCls} placeholder="Mills, WY" data-testid="quote-to" />
        </Field>
        <Field label="Move Type" required>
          <select name="move_type" value={form.move_type} onChange={onChange} required className={inputCls} data-testid="quote-type">
            <option value="residential">Residential (Home / Apartment)</option>
            <option value="commercial">Commercial / Office</option>
          </select>
        </Field>
        <Field label="Size of Move">
          <select name="size" value={form.size} onChange={onChange} className={inputCls} data-testid="quote-size">
            <option value="">Select size</option>
            <option>Studio / 1 Bedroom</option>
            <option>2 Bedroom</option>
            <option>3 Bedroom</option>
            <option>4+ Bedroom</option>
            <option>Small Office</option>
            <option>Large Office</option>
            <option>Single Item / Heavy Lifting</option>
          </select>
        </Field>
        <Field label="Stairs / Access">
          <input name="stairs" value={form.stairs} onChange={onChange} className={inputCls} placeholder="2 flights, elevator, tight hallway..." data-testid="quote-stairs" />
        </Field>
        <Field label="Packing / White-Glove Needs">
          <select name="packing_needs" value={form.packing_needs} onChange={onChange} className={inputCls} data-testid="quote-packing">
            <option value="">Select packing service</option>
            <option>No packing needed</option>
            <option>Partial packing</option>
            <option>Full packing & unpacking</option>
            <option>White-glove handling</option>
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Special Items (piano, safe, antiques, appliances...)">
            <input name="special_items" value={form.special_items} onChange={onChange} className={inputCls} placeholder="Upright piano, gun safe, 2 appliances" data-testid="quote-special" />
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Notes / Additional Details">
            <textarea name="message" value={form.message} onChange={onChange} rows={4} className={inputCls} placeholder="Anything else we should know about your move?" data-testid="quote-message" />
          </Field>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary mt-8 w-full md:w-auto"
        data-testid="quote-submit"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Get My Free Quote <Send className="w-4 h-4" /></>}
      </button>
      <p className="mt-4 text-xs text-[var(--ct-text-muted)]">
        We respect your privacy. No spam. A real Wyoming mover will reach out with a free estimate.
      </p>
      <p className="mt-2 text-[11px] text-[var(--ct-text-muted)] leading-relaxed">
        By submitting, you agree to receive SMS messages from Crosstown Movers about your quote.
        Msg &amp; data rates may apply. Msg frequency varies. Reply <strong>STOP</strong> to opt out,{" "}
        <strong>HELP</strong> for help. See our{" "}
        <a href="/sms-terms" className="underline hover:text-[var(--ct-primary)]">SMS Terms</a>.
      </p>
    </form>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--ct-primary)] mb-2 block">
      {label} {required && <span className="text-[var(--ct-secondary)]">*</span>}
    </span>
    {children}
  </label>
);

export default QuoteForm;
